// AGC: DeleteConfirmContext - Global "confirm before delete" setting and modal
// AGC: When user enables "Confirm before delete" in Settings > Segurança > Sistema,
// AGC: any delete action shows a confirmation modal (red Confirm, normal Cancel).

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { useTranslation } from '../hooks/useTranslation'

const STORAGE_KEY = 'droidfarm_confirm_before_delete'

const getStored = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

interface PendingConfirm {
  onConfirm: () => void | Promise<void>
  message?: string
}

interface DeleteConfirmContextType {
  /** AGC: Whether to show confirmation popup before any delete (default false). */
  confirmBeforeDelete: boolean
  /** AGC: Toggle the setting (persisted to localStorage). */
  setConfirmBeforeDelete: (value: boolean) => void
  /**
   * AGC: Run delete action: if confirmBeforeDelete is OFF, runs onConfirm immediately.
   * AGC: If ON, shows modal with red Confirm and normal Cancel.
   */
  requestConfirmDelete: (options: PendingConfirm) => void
}

const DeleteConfirmContext = createContext<DeleteConfirmContextType | undefined>(undefined)

export const useDeleteConfirm = (): DeleteConfirmContextType => {
  const ctx = useContext(DeleteConfirmContext)
  if (ctx === undefined) {
    throw new Error('AGC: useDeleteConfirm must be used within DeleteConfirmProvider')
  }
  return ctx
}

interface DeleteConfirmProviderProps {
  children: ReactNode
}

/**
 * AGC: Provides confirm-before-delete setting and modal for delete actions.
 * AGC: Wrap app content so any screen can use useDeleteConfirm().requestConfirmDelete().
 */
export const DeleteConfirmProvider: React.FC<DeleteConfirmProviderProps> = ({ children }) => {
  const [confirmBeforeDelete, setConfirmBeforeDeleteState] = useState<boolean>(getStored)
  const [pending, setPending] = useState<PendingConfirm | null>(null)

  const setConfirmBeforeDelete = useCallback((value: boolean) => {
    setConfirmBeforeDeleteState(value)
    try {
      if (value) localStorage.setItem(STORAGE_KEY, 'true')
      else localStorage.removeItem(STORAGE_KEY)
    } catch {
      // AGC: Ignore localStorage errors
    }
  }, [])

  const requestConfirmDelete = useCallback((options: PendingConfirm) => {
    if (!confirmBeforeDelete) {
      void Promise.resolve(options.onConfirm()).catch((err) => console.error('AGC: Delete failed', err))
      return
    }
    setPending(options)
  }, [confirmBeforeDelete])

  const handleConfirm = useCallback(async () => {
    if (pending) {
      try {
        await Promise.resolve(pending.onConfirm())
      } catch (err) {
        console.error('AGC: Delete failed', err)
      }
      setPending(null)
    }
  }, [pending])

  const handleCancel = useCallback(() => {
    setPending(null)
  }, [])

  const value: DeleteConfirmContextType = {
    confirmBeforeDelete,
    setConfirmBeforeDelete,
    requestConfirmDelete,
  }

  return (
    <DeleteConfirmContext.Provider value={value}>
      {children}
      {/* AGC: Global confirm-delete modal - red Confirm, normal Cancel */}
      {pending && (
        <ConfirmDeleteModal
          message={pending.message}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </DeleteConfirmContext.Provider>
  )
}

// AGC: Modal UI for delete confirmation (red Confirm button, normal Cancel)
interface ConfirmDeleteModalProps {
  message?: string
  onConfirm: () => void
  onCancel: () => void
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({ message, onConfirm, onCancel }) => {
  const { t } = useTranslation()

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-delete-title"
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 id="confirm-delete-title" className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('profile.confirmDeleteTitle')}
          </h3>
          {message && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{message}</p>
          )}
        </div>
        <div className="p-4 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-xl font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 rounded-xl font-medium text-white bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors"
          >
            {t('profile.confirmDeleteButton')}
          </button>
        </div>
      </div>
    </div>
  )
}
